import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "@src/api/config";
import { showModalSuccess } from "../../../redux/modalTypeSuccess";
import { AppDispatch, RootState } from "@src/redux/store";

const urlBase = "api/company_clients";

type RequestParams = Record<string, any>;

export interface RCClientPayload {
  nombre: string;
  correo: string;
  telefono: string;
  ciudad: string;
  company_id?: number;
  from?: string;
  [key: string]: any;
}

export interface RCClientsState {
  data: any;
  suggestions: any[];
  defaultValues: RCClientPayload | null;
  currentPage: number;
  params: RequestParams;
  meta: any;
  total: number;
}

export const getRCClients = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("rcClients/getAll", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(urlBase, { params: query });
  return {
    params: query,
    data: response.data
  };
});

export const getRCClientSuggestions = createAsyncThunk<
  any,
  RequestParams | undefined,
  { state: RootState }
>("rcClients/getSuggestions", async (params = {}, { getState }) => {
  const state = getState();
  const { productSelected } = state.products || {};
  const { businessLineSelected } = state.businessLines || {};

  const query = {
    ...params,
    product: productSelected?.value,
    business_line: businessLineSelected?.id
  };

  const response = await api.get(`${urlBase}/suggestions`, { params: query });
  return {
    data: response.data
  };
});

export const getRCClientById = createAsyncThunk<any, number>(
  "rcClients/getById",
  async (id: number) => {
    const response = await api.get(`${urlBase}/${id}`);
    return {
      data: response.data
    };
  }
);

export const createRCClient = createAsyncThunk<
  any,
  RCClientPayload,
  { dispatch: AppDispatch; state: RootState }
>("rcClients/create", async (payload, { dispatch, getState }) => {
  const state = getState();
  const companyId = state.auth?.userData?.role_id ?? payload.company_id;

  const body = {
    company_id: companyId,
    name: payload.nombre,
    email: payload.correo,
    phone: payload.telefono,
    city: payload.ciudad
  };

  const response = await api.post(urlBase, body);

  if (response.data.status === "success") {
    if (payload.from === "providers") {
      dispatch(getRCClients({ format: "options", free: true }));
    }
    await dispatch(
      showModalSuccess(
        payload.from === "providers"
          ? { message: response.data.message }
          : { message: response.data.message, url: "goBack" }
      )
    );
  }

  return {
    data: response.data
  };
});

export const updateRCClient = createAsyncThunk<
  any,
  { id: number | string; data: RCClientPayload },
  { dispatch: AppDispatch; state: RootState }
>("rcClients/update", async ({ id, data }, { dispatch, getState }) => {
  const state = getState();
  const companyId = state.auth?.userData?.role_id ?? data.company_id;

  const body = {
    company_id: companyId,
    name: data.nombre,
    email: data.correo,
    phone: data.telefono,
    city: data.ciudad
  };

  const response = await api.put(`${urlBase}/${id}`, body);

  if (response.data.status === "success") {
    const url = "goBack";
    dispatch(showModalSuccess({ message: response.data.message, url }));
  }

  return {
    data: response.data
  };
});

export const deleteRCClient = createAsyncThunk<
  any,
  number,
  { dispatch: AppDispatch }
>("rcClients/delete", async (id, { dispatch }) => {
  const response = await api.delete(`${urlBase}/${id}`);

  if (response.data.status === "success") {
    dispatch(showModalSuccess({ message: response.data.message }));
    await dispatch(getRCClients());
  }

  return {
    data: response.data
  };
});

const initialState: RCClientsState = {
  data: [],
  suggestions: [],
  defaultValues: null,
  currentPage: 1,
  params: {},
  meta: {},
  total: 0
};

export const rcClientsSlice = createSlice({
  name: "rcClients",
  initialState,
  reducers: {
    resetRCClients: (state) => {
      state.data = [];
      state.suggestions = [];
      state.defaultValues = null;
      state.currentPage = 1;
      state.params = {};
      state.meta = {};
      state.total = 0;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getRCClients.fulfilled, (state, action) => {
      if (action.payload?.params?.format === "options") {
        state.suggestions = action.payload.data;
      } else {
        state.data = action.payload.data;
        state.currentPage = action.payload.data?.current_page;
        state.params = action.payload.params;
        state.meta = action.payload.data?.meta;
        state.total = action.payload.data?.meta?.total;
      }
    });
    builder.addCase(getRCClientById.fulfilled, (state, action) => {
      const payloadData = action.payload?.data?.data || {};
      state.defaultValues = {
        nombre: payloadData.nombre ?? payloadData.name ?? "",
        correo: payloadData.correo ?? payloadData.email ?? "",
        telefono: payloadData.telefono ?? payloadData.phone ?? "",
        ciudad: payloadData.ciudad ?? payloadData.city ?? ""
      };
    });
    builder.addCase(getRCClientSuggestions.fulfilled, (state, action) => {
      state.suggestions = action.payload.data;
    });
  }
});

export const { resetRCClients } = rcClientsSlice.actions;

export default rcClientsSlice.reducer;
